import React, { useState } from "react";
import { BookOpen, ChevronRight, Stethoscope, Share2, Copy, Check, ExternalLink, X, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { DEFAULT_BAD_LEAFLETS } from "../data/badNewsItems";

export default function MedicalNewsBanner({ onOpenDoctorsPortal, whiteLabel }: { onOpenDoctorsPortal: () => void; whiteLabel?: any }) {
  const [selectedNews, setSelectedNews] = useState<any | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // EXPERT COMMENTARY: Ensure Medical News Banner always renders cleanly on mobile and tablets without being blocked by initial config loading states.
  const news: any[] = (whiteLabel?.clinicNewsItems && Array.isArray(whiteLabel.clinicNewsItems) && whiteLabel.clinicNewsItems.length > 0)
    ? whiteLabel.clinicNewsItems
    : DEFAULT_BAD_LEAFLETS;
  
  if (!news || news.length === 0) return null;

  const displayedNews = showAll ? news : news.slice(0, 3);

  const handleCopy = (item: any) => {
    const textToCopy = `${item.title}\n\n${item.description}\n\nالمصدر: الجمعية البريطانية لأمراض الجلد (BAD) | التثقيف الصحي - موقع د. واثق الجلبي (IQ-Doctor)`;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleWhatsAppShare = (item: any) => {
    const shareText = `${item.title}\n\n${item.description}\n\nالمصدر: الجمعية البريطانية لأمراض الجلد (BAD) | التثقيف الصحي - موقع د. واثق الجلبي (IQ-Doctor)`;
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="w-full bg-slate-900 py-12 px-4 border-t-4 border-indigo-500 mt-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center sm:items-end gap-5 sm:gap-4 mb-8" dir="rtl">
          <div className="text-center sm:text-right w-full sm:w-auto">
            <div className="flex items-center justify-center sm:justify-start gap-3 mb-1.5 sm:mb-1">
              <div className="bg-indigo-500/20 p-2.5 rounded-xl shrink-0">
                <Stethoscope className="w-6 h-6 text-indigo-400" />
              </div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">البوابة الطبية</h2>
            </div>
            <p className="text-indigo-300 text-xs sm:text-sm md:text-base font-bold sm:pr-11">التثقيف الصحي - نشرات معلومات المريض</p>
          </div>
          <button 
            onClick={onOpenDoctorsPortal}
            className="w-full sm:w-auto justify-center bg-indigo-600 hover:bg-indigo-500 active:scale-95 text-white px-6 py-3 rounded-xl font-extrabold text-sm transition-all flex items-center gap-2.5 shadow-lg shadow-indigo-500/25 group shrink-0"
          >
            <span>الدخول للبوابة الطبية</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform rotate-180 shrink-0" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7" dir="rtl">
          {displayedNews.map((item, index) => (
            <motion.div 
              key={item.id || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (index % 3) * 0.1 }}
              className="bg-slate-800 rounded-2xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-all group shadow-xl flex flex-col justify-between"
              onClick={() => setSelectedNews(item)}
            >
              <div>
                <div className="h-48 w-full overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-10" />
                  <img 
                    src={item.imageUrl || "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=800"} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=800";
                    }}
                  />
                  <div className="absolute bottom-3 right-3 z-20 bg-indigo-600/90 text-white text-[11px] font-bold px-2.5 py-1 rounded-full backdrop-blur-sm">
                    نشرة طبية
                  </div>
                </div>
                <div className="p-5 relative z-20 text-right">
                  <h3 className="text-white font-bold text-base mb-2.5 line-clamp-2 leading-relaxed group-hover:text-indigo-400 transition-colors">{item.title}</h3>
                  <p className="text-slate-400 text-xs md:text-sm line-clamp-3 leading-relaxed font-medium">{item.description}</p>
                </div>
              </div>
              <div className="px-5 pb-4 pt-2 flex items-center justify-between text-indigo-400 text-xs font-bold border-t border-slate-700/60 mt-2">
                <span>قراءة كامل النشرة والمشاركة</span>
                <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>

        {news.length > 3 && (
          <div className="mt-10 flex justify-center" dir="rtl">
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="bg-slate-800 hover:bg-slate-750 text-indigo-300 hover:text-white border border-slate-700 px-7 py-3 rounded-2xl font-black text-sm flex items-center gap-2.5 transition-all duration-200 shadow-xl hover:shadow-indigo-500/10 cursor-pointer group"
            >
              <span>{showAll ? "إخفاء الصور والنشرات الإضافية (عرض 3 فقط)" : `عرض المزيد من الصور والنشرات (${news.length - 3} إضافية)`}</span>
              <ChevronRight className={`w-5 h-5 text-indigo-400 group-hover:text-amber-400 transition-transform duration-300 ${showAll ? "-rotate-90" : "rotate-90"}`} />
            </button>
          </div>
        )}

        {/* News & Patient Information Modal */}
        <AnimatePresence>
          {selectedNews && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" dir="rtl" onClick={() => setSelectedNews(null)}>
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100 flex flex-col relative"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header Image */}
                <div className="relative h-64 w-full bg-slate-900 shrink-0">
                  <img 
                    src={selectedNews.imageUrl || "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=800"} 
                    alt={selectedNews.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
                  
                  <button 
                    onClick={() => setSelectedNews(null)}
                    className="absolute top-4 left-4 bg-black/50 hover:bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center transition backdrop-blur-md"
                    title="إغلاق النافذة"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  <div className="absolute bottom-4 right-6 left-6 text-right">
                    <div className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-2 shadow-sm">
                      <span>نشرة معلومات المريض - التثقيف الصحي</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-extrabold text-white leading-snug">{selectedNews.title}</h3>
                  </div>
                </div>

                {/* Modal Body Content */}
                <div className="p-6 md:p-8 text-right flex-1 space-y-6">
                  <div className="text-slate-700 text-sm md:text-base leading-loose whitespace-pre-line font-medium bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    {selectedNews.description}
                  </div>

                  {selectedNews.link && (
                    <div className="pt-2">
                      <a 
                        href={selectedNews.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold px-4 py-2.5 rounded-xl text-sm transition"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>فتح الرابط المرفق للمزيد من المعلومات</span>
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

